"use client";

import classNames from "classnames";
import { useState, useEffect } from "react";
import { Button as AriaButton } from "react-aria-components";
import { Spinner, Text } from "ui-components";

import { useAuth } from "@/hooks/useAuth";
import { ProfileSettingsTabsEnum } from "@/src/@types/Profile";

import MyAccountSettings from "./MyAccountSettings";
import { MyBlockMembers } from "./MyBlockMembers";
import MyNewsletterSettings from "./MyNewsletterSettings";
import MyNotificationSettings from "./MyNotificationSettings";

const TABS = [
  {
    title: "My Account",
    key: ProfileSettingsTabsEnum.MY_ACCOUNT,
  },
  {
    title: "Newsletter",
    key: ProfileSettingsTabsEnum.NEWSLETTER,
  },
  {
    title: "Notifications",
    key: ProfileSettingsTabsEnum.NOTIFICATIONS,
  },
  {
    title: "Blocked Members",
    key: ProfileSettingsTabsEnum.BLOCK_MEMBERS,
  },
];

export default function MySettings(): JSX.Element {
  const { authData } = useAuth();
  const [currentTab, setCurrentTab] = useState<ProfileSettingsTabsEnum>(
    ProfileSettingsTabsEnum.MY_ACCOUNT
  );
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const hash = window.location.hash.slice(1);
    const tab = TABS.find(
      (activeTab) => activeTab.title.toLowerCase().replace(/\s+/g, "-") === hash
    );
    if (tab) {
      setCurrentTab(tab.key);
    }
  }, []);

  const handleTabClick = (
    tabKey: ProfileSettingsTabsEnum,
    tabTitle: string
  ): void => {
    setCurrentTab(tabKey);
    const formattedTitle = tabTitle.toLowerCase().replace(/\s+/g, "-");
    window.location.hash = formattedTitle;
  };

  if (!isClient) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center w-full p-10  max-h-[80vh] gap-4 overflow-y-auto ">
        <Spinner />
      </div>
    );
  }

  if (authData?.type === "agent") {
    return <MyAccountSettings />;
  }

  return (
    <div className="flex mb-20">
      <div className="w-1/5 flex flex-col gap-y-5 lg:absolute">
        <Text className="!font-bold">Update your settings</Text>
        {TABS.map((tab) => (
          <AriaButton
            key={tab.key}
            className="text-left outline-none"
            onPress={() => handleTabClick(tab.key, tab.title)}
          >
            <Text
              className={classNames(
                tab.key === currentTab ? "border-b-2 border-primary" : "",
                "py-2"
              )}
            >
              {tab.title}
            </Text>
          </AriaButton>
        ))}
      </div>
      <div className="w-full">
        {currentTab === ProfileSettingsTabsEnum.MY_ACCOUNT && (
          <MyAccountSettings />
        )}
        {currentTab === ProfileSettingsTabsEnum.NEWSLETTER && (
          <MyNewsletterSettings />
        )}
        {currentTab === ProfileSettingsTabsEnum.NOTIFICATIONS && (
          <MyNotificationSettings />
        )}
        {currentTab === ProfileSettingsTabsEnum.BLOCK_MEMBERS && (
          <MyBlockMembers />
        )}
      </div>
    </div>
  );
}
